import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  DollarSign, 
  AlertCircle, 
  FileText, 
  Settings, 
  BarChart2, 
  ShieldCheck, 
  Download, 
  LogOut, 
  Users, 
  MessageSquare, 
  Crown,
  Menu,
  X,
  Calculator,
  Award,
  BookOpen,
  Fingerprint,
  Upload,
  CheckSquare,
  Coins,
  UserPlus,
  Building2
} from 'lucide-react';
import { useFirebase } from '../contexts/FirebaseContext';
import { signOut } from 'firebase/auth';
import { auth, db } from '../firebase';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { getUnreadMessageCount } from '../services/messageService';
import { getPendingCashTips } from '../services/cashTipService';
import { getPendingJarTips } from '../services/jarTipService';
import { getPendingDisputes, getPendingDisputesByEmail } from '../services/disputeService';
import { getPendingAllocations } from '../services/allocationService';
import { getPendingReviewTipsCount } from '../services/approvalService';
import { getPayrollPeriods } from '../services/payrollPeriodService';
import { motion, AnimatePresence } from 'motion/react';
import { getSettings } from '../services/settingsService';

export const Sidebar: React.FC = () => {
  const { role, user, tenantId } = useFirebase();
  const [unreadCount, setUnreadCount] = useState(0);
  const [tipCount, setTipCount] = useState(0);
  const [jarTipCount, setJarTipCount] = useState(0);
  const [reviewApproveCount, setReviewApproveCount] = useState(0);
  const [disputeCount, setDisputeCount] = useState(0);
  const [allocationCount, setAllocationCount] = useState(0);
  const [approvalCount, setApprovalCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [requireIRS8PercentCompliance, setRequireIRS8PercentCompliance] = useState<boolean>(true);
  const navigate = useNavigate();
  const isSuperAdmin = user?.email?.toLowerCase() === 'admin@growezy.ai';
  const isAdminOrManager = role === 'admin' || role === 'manager' || isSuperAdmin;

  useEffect(() => {
    if (tenantId) {
      getSettings(tenantId).then(data => {
        if (data && data.requireIRS8PercentCompliance !== undefined) {
          setRequireIRS8PercentCompliance(data.requireIRS8PercentCompliance);
        } else {
          setRequireIRS8PercentCompliance(true); // Default to true so existing accounts are unaffected
        }
      }).catch(err => {
        console.error("Error loading settings in Sidebar:", err);
      });
    }
  }, [tenantId]);

  const fetchData = React.useCallback(async () => {
    if (!tenantId || !user?.uid) return;
    const [unread, tips, jarTips, disputes, allocs, reviewCount] = await Promise.all([
      getUnreadMessageCount(tenantId, user.uid),
      getPendingCashTips(tenantId),
      isAdminOrManager ? getPendingJarTips(tenantId) : Promise.resolve([]),
      isAdminOrManager
        ? getPendingDisputes(tenantId)
        : (user.email ? getPendingDisputesByEmail(tenantId, user.email) : Promise.resolve([])),
      getPendingAllocations(tenantId),
      isAdminOrManager ? getPendingReviewTipsCount(tenantId) : Promise.resolve(0)
    ]);
    setUnreadCount(unread);
    setTipCount(tips.length);
    setJarTipCount(jarTips.length);
    setDisputeCount(disputes.length);
    setAllocationCount(allocs.length);
    if (isAdminOrManager) {
      setReviewApproveCount(reviewCount);
    }
  }, [tenantId, user?.uid, user?.email, isAdminOrManager]);

  useEffect(() => {
    if (!tenantId || !isAdminOrManager) {
      setReviewApproveCount(0);
      return;
    }

    let cashCount = 0;
    let jarCount = 0;
    let posCount = 0;

    const updateCount = () => {
      setReviewApproveCount(cashCount + jarCount + posCount);
    };

    const cashRef = collection(db, 'tenants', tenantId, 'cash_tips');
    const cashQuery = query(cashRef, where('status', '==', 'Pending'));
    const unsubCash = onSnapshot(cashQuery, snap => {
      cashCount = snap.size;
      updateCount();
    }, err => console.warn('Sidebar cash tips count error:', err));

    const jarRef = collection(db, 'tenants', tenantId, 'jar_tips');
    const jarQuery = query(jarRef, where('status', '==', 'Pending'));
    const unsubJar = onSnapshot(jarQuery, snap => {
      jarCount = snap.size;
      updateCount();
    }, err => console.warn('Sidebar jar tips count error:', err));

    const posRef = collection(db, 'tenants', tenantId, 'master_transactions');
    const posQuery = query(posRef, where('source', 'in', ['POS', 'POS Override']), where('submissionStatus', '==', 'Pending'));
    const unsubPos = onSnapshot(posQuery, snap => {
      posCount = snap.size;
      updateCount();
    }, err => console.warn('Sidebar pos tips count error:', err));

    return () => {
      unsubCash();
      unsubJar();
      unsubPos();
    };
  }, [tenantId, isAdminOrManager]);

  useEffect(() => {
    if (tenantId && user?.uid) {
      // Small delay to ensure the UI renders first
      const timeout = setTimeout(fetchData, 500);
      const interval = setInterval(fetchData, 60000); // 1 minute
      return () => {
        clearTimeout(timeout);
        clearInterval(interval);
      };
    }
  }, [fetchData, tenantId, user?.uid]);

  const desktopLinkClass = ({ isActive }: { isActive: boolean }) => 
    `flex items-center space-x-3 p-3 rounded-xl transition-all ${
      isActive 
        ? "text-blue-400 bg-blue-500/10 font-semibold" 
        : "text-gray-400 hover:text-white hover:bg-gray-800"
    }`;

  const mobileLinkClass = ({ isActive }: { isActive: boolean }) => 
    `flex items-center space-x-4 px-4 py-3.5 rounded-2xl transition-all text-base min-h-[48px] ${
      isActive 
        ? "text-blue-400 bg-blue-500/10 font-bold" 
        : "text-gray-300 hover:text-white hover:bg-gray-850"
    }`;

  const mobileBottomLinkClass = ({ isActive }: { isActive: boolean }) => 
    `flex flex-col items-center justify-center flex-1 py-1 px-1 transition-all relative min-h-[48px] cursor-pointer ${
      isActive 
        ? "text-blue-400 font-bold" 
        : "text-gray-500 hover:text-gray-300 font-medium"
    }`;

  const handleMobileLinkClick = () => {
    setMobileMenuOpen(false);
  };

  const handleSignOut = async () => {
    try {
      setMobileMenuOpen(false);
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  return (
    <div className="no-print">
      {/* ========================================== */}
      {/* MOBILE HEADER & BURGER TABS (Viewport < 768px) */}
      {/* ========================================== */}
      <div className="fixed top-0 left-0 right-0 h-16 bg-gray-900 border-b border-gray-800 z-50 px-4 flex items-center justify-between md:hidden shadow-lg">
        <div className="flex items-center space-x-2.5">
          <div className="w-8 h-8 bg-blue-650 rounded-lg flex items-center justify-center shadow-inner">
            <DollarSign size={18} className="text-white" />
          </div>
          <span className="text-lg font-black text-white tracking-tight">TipValue AI</span>
          <span className="px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400 border border-blue-500/30 text-[9px] font-black uppercase tracking-wider">
            BETA
          </span>
        </div>

        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="relative p-2 text-gray-300 hover:text-white rounded-xl hover:bg-gray-800 transition-colors focus:outline-none min-h-[44px] min-w-[44px] flex items-center justify-center cursor-pointer"
          aria-label="Toggle Menu"
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          
          {/* Badge for unread chat count - visible on primary hamburger menu icon */}
          {!mobileMenuOpen && unreadCount > 0 && (
            <span className="absolute top-1 right-1 flex h-4.5 w-4.5 items-center justify-center rounded-full bg-red-500 text-[9px] font-black text-white ring-2 ring-gray-900">
              {unreadCount}
            </span>
          )}
        </button>
      </div>

      {/* ========================================== */}
      {/* MOBILE BOTTOM-FIXED NAVIGATION BAR */}
      {/* ========================================== */}
      <div className="fixed bottom-0 left-0 right-0 h-16 bg-gray-950/95 backdrop-blur-md border-t border-gray-850 z-40 px-2 flex items-center justify-around md:hidden shadow-2xl safe-bottom">
        <NavLink 
          to="/" 
          className={mobileBottomLinkClass}
          onClick={handleMobileLinkClick}
        >
          {({ isActive }) => (
            <>
              <LayoutDashboard size={20} className={isActive ? "text-blue-400" : "text-gray-500"} />
              <span className="text-[10px] mt-0.5 tracking-tight truncate">
                {isAdminOrManager ? "Ops Dash" : "Dashboard"}
              </span>
            </>
          )}
        </NavLink>

        <NavLink 
          to="/personal" 
          className={mobileBottomLinkClass}
          onClick={handleMobileLinkClick}
        >
          {({ isActive }) => (
            <>
              <Users size={20} className={isActive ? "text-blue-400" : "text-gray-500"} />
              <span className="text-[10px] mt-0.5 tracking-tight truncate">My Tips</span>
            </>
          )}
        </NavLink>

        <NavLink 
          to="/approvals" 
          className={mobileBottomLinkClass}
          onClick={handleMobileLinkClick}
        >
          {({ isActive }) => (
            <>
              <Fingerprint size={20} className={isActive ? "text-blue-400" : "text-gray-500"} />
              <span className="text-[10px] mt-0.5 tracking-tight truncate">Approvals</span>
            </>
          )}
        </NavLink>

        <NavLink 
          to="/communication" 
          className={mobileBottomLinkClass}
          onClick={handleMobileLinkClick}
        >
          {({ isActive }) => (
            <>
              <div className="relative">
                <MessageSquare size={20} className={isActive ? "text-blue-400" : "text-gray-500"} />
                {unreadCount > 0 && (
                  <span className="absolute -top-1.5 -right-2 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[8px] font-black text-white">
                    {unreadCount}
                  </span>
                )}
              </div>
              <span className="text-[10px] mt-0.5 tracking-tight truncate">Chat</span>
            </>
          )}
        </NavLink>

        <button 
          onClick={() => setMobileMenuOpen(prev => !prev)}
          className={`flex flex-col items-center justify-center flex-1 py-1 px-1 transition-all cursor-pointer min-h-[48px] ${
            mobileMenuOpen ? "text-blue-400 font-bold" : "text-gray-500 hover:text-gray-300 font-medium"
          }`}
        >
          {mobileMenuOpen ? (
            <X size={20} className="text-blue-400" />
          ) : (
            <Menu size={20} className="text-gray-500" />
          )}
          <span className="text-[10px] mt-0.5 tracking-tight truncate">Menu</span>
        </button>
      </div>

      {/* MOBILE DRAWER OVERLAY & VERTICAL MENU (Animate Slide & Fade) */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            {/* Backdrop cover */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileMenuOpen(false)}
              className="fixed inset-0 bg-black z-40 md:hidden"
            />

            {/* Sliding Drawer Content */}
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 220 }}
              className="fixed inset-y-0 left-0 w-[280px] max-w-[85vw] bg-gray-900 border-r border-gray-850 z-45 md:hidden flex flex-col pt-16 shadow-2xl"
            >
              {/* Scrollable area with huge, clean tap targets */}
              <div className="flex-1 overflow-y-auto py-6 px-4 space-y-1 pb-20 no-scrollbar">
                
                {isAdminOrManager ? (
                  <>
                    {/* SECTION 1: DAILY OPERATIONS */}
                    <p className="text-[10px] font-black uppercase text-gray-500 tracking-widest px-4 pb-1">Daily Operations</p>
                    
                    <NavLink to="/" className={mobileLinkClass} onClick={handleMobileLinkClick}>
                      <LayoutDashboard size={20} className="text-gray-400 shrink-0" />
                      <span className="font-semibold">Operations Dashboard</span>
                    </NavLink>

                    <NavLink to="/jar-tips" className={mobileLinkClass} onClick={handleMobileLinkClick}>
                      <div className="relative flex items-center gap-4">
                        <Coins size={20} className="text-gray-400 shrink-0" />
                        <span className="font-semibold">Submit Jar Cash Tips</span>
                      </div>
                    </NavLink>

                    <NavLink to="/pos-import" className={mobileLinkClass} onClick={handleMobileLinkClick}>
                      <div className="relative flex items-center gap-4">
                        <Upload size={20} className="text-gray-400 shrink-0" />
                        <span className="font-semibold">Import POS Tips</span>
                      </div>
                    </NavLink>

                    <NavLink to="/review-approve" className={mobileLinkClass} onClick={handleMobileLinkClick}>
                      <div className="relative flex items-center gap-4">
                        <CheckSquare size={20} className="text-gray-400 shrink-0" />
                        <span className="font-semibold">Review & Approve Tips</span>
                        {reviewApproveCount > 0 && (
                          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-amber-500 text-[10px] font-black text-white ml-2">
                            {reviewApproveCount}
                          </span>
                        )}
                      </div>
                    </NavLink>

                    <NavLink to="/allocations" className={mobileLinkClass} onClick={handleMobileLinkClick}>
                      <div className="relative flex items-center gap-4">
                        <ShieldCheck size={20} className="text-gray-400 shrink-0" />
                        <span className="font-semibold">Review Allocations</span>
                        {allocationCount > 0 && (
                          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-black text-white ml-2">
                            {allocationCount}
                          </span>
                        )}
                      </div>
                    </NavLink>

                    <NavLink to="/disputes" className={mobileLinkClass} onClick={handleMobileLinkClick}>
                      <div className="relative flex items-center gap-4">
                        <AlertCircle size={20} className="text-gray-400 shrink-0" />
                        <span className="font-semibold">Dispute Management</span>
                        {disputeCount > 0 && (
                          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-black text-white ml-2">
                            {disputeCount}
                          </span>
                        )}
                      </div>
                    </NavLink>

                    {/* SECTION 2: PAYROLL & REPORTING */}
                    <hr className="my-3 border-gray-800" />
                    <p className="text-[10px] font-black uppercase text-gray-500 tracking-widest px-4 pb-1">Payroll and Reporting</p>

                    <NavLink to="/payroll" className={mobileLinkClass} onClick={handleMobileLinkClick}>
                      <Download size={20} className="text-gray-400 shrink-0" />
                      <span className="font-semibold">Run Tip Payroll Export</span>
                    </NavLink>

                    <NavLink to="/reports" className={mobileLinkClass} onClick={handleMobileLinkClick}>
                      <FileText size={20} className="text-gray-400 shrink-0" />
                      <span className="font-semibold">Reports</span>
                    </NavLink>

                    <NavLink to="/shortfall" className={mobileLinkClass} onClick={handleMobileLinkClick}>
                      <Calculator size={20} className="text-gray-400 shrink-0" />
                      <span className="font-semibold">Gross Receipts & IRS 8%</span>
                    </NavLink>

                    {/* SECTION 3: ANALYTICS & TEAM */}
                    <hr className="my-3 border-gray-800" />
                    <p className="text-[10px] font-black uppercase text-gray-500 tracking-widest px-4 pb-1">Analytics & Team</p>

                    <NavLink to="/benchmarks" className={mobileLinkClass} onClick={handleMobileLinkClick}>
                      <BarChart2 size={20} className="text-gray-400 shrink-0" />
                      <span className="font-semibold">Benchmark Dashboard</span>
                    </NavLink>

                    <NavLink to="/communication" className={mobileLinkClass} onClick={handleMobileLinkClick}>
                      <div className="relative shrink-0">
                        <MessageSquare size={20} className="text-gray-400" />
                        {unreadCount > 0 && (
                          <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[9px] font-black text-white">
                            {unreadCount}
                          </span>
                        )}
                      </div>
                      <span className="font-semibold">Chat</span>
                    </NavLink>

                    {/* SECTION 4: PERSONAL ACCOUNT */}
                    <hr className="my-3 border-gray-800" />
                    <p className="text-[10px] font-black uppercase text-gray-500 tracking-widest px-4 pb-1">Personal Account</p>

                    <NavLink to="/personal" className={mobileLinkClass} onClick={handleMobileLinkClick}>
                      <Users size={20} className="text-gray-400 shrink-0" />
                      <span className="font-semibold">My Tips</span>
                    </NavLink>

                    <NavLink to="/tips" className={mobileLinkClass} onClick={handleMobileLinkClick}>
                      <div className="relative flex items-center gap-4">
                        <DollarSign size={20} className="text-gray-400 shrink-0" />
                        <span className="font-semibold">Report My Cash Tips</span>
                        {tipCount > 0 && (
                          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-black text-white ml-2">
                            {tipCount}
                          </span>
                        )}
                      </div>
                    </NavLink>

                    <NavLink to="/approvals" className={mobileLinkClass} onClick={handleMobileLinkClick}>
                      <Fingerprint size={20} className="text-gray-400 shrink-0" />
                      <span className="font-semibold">Approval & Sign-off</span>
                    </NavLink>

                    {/* SECTION 5: MANAGEMENT */}
                    <hr className="my-3 border-gray-800" />
                    <p className="text-[10px] font-black uppercase text-gray-500 tracking-widest px-4 pb-1">Management</p>

                    <NavLink to="/staff-roster" className={mobileLinkClass} onClick={handleMobileLinkClick}>
                      <Users size={20} className="text-gray-400 shrink-0" />
                      <span className="font-semibold">Staff & Roster</span>
                    </NavLink>

                    {(role === 'admin' || role === 'owner' || isSuperAdmin) && (
                      <NavLink to="/settings/locations" className={mobileLinkClass} onClick={handleMobileLinkClick}>
                        <Building2 size={20} className="text-gray-400 shrink-0" />
                        <span className="font-semibold">Store Locations</span>
                      </NavLink>
                    )}

                    {(role === 'admin' || role === 'owner' || isSuperAdmin) && (
                      <NavLink to="/user-management" className={mobileLinkClass} onClick={handleMobileLinkClick}>
                        <UserPlus size={20} className="text-gray-400 shrink-0" />
                        <span className="font-semibold">Invite Users</span>
                      </NavLink>
                    )}

                    {(role === 'owner' || isSuperAdmin) && (
                      <NavLink to="/settings" className={mobileLinkClass} onClick={handleMobileLinkClick}>
                        <Settings size={20} className="text-gray-400 shrink-0" />
                        <span className="font-semibold">Settings</span>
                      </NavLink>
                    )}
                  </>
                ) : (
                  /* ========================================== */
                  /* EMPLOYEE MOBILE DRAWER NAVIGATION          */
                  /* ========================================== */
                  <>
                    <p className="text-[10px] font-black uppercase text-gray-500 tracking-widest px-4 pb-1">My Workspace</p>

                    <NavLink to="/" className={mobileLinkClass} onClick={handleMobileLinkClick}>
                      <LayoutDashboard size={20} className="text-gray-400 shrink-0" />
                      <span className="font-semibold">My Dashboard</span>
                    </NavLink>

                    <NavLink to="/tips" className={mobileLinkClass} onClick={handleMobileLinkClick}>
                      <div className="relative flex items-center gap-4">
                        <DollarSign size={20} className="text-gray-400 shrink-0" />
                        <span className="font-semibold">Report My Cash Tips</span>
                        {tipCount > 0 && (
                          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-black text-white ml-2">
                            {tipCount}
                          </span>
                        )}
                      </div>
                    </NavLink>

                    <NavLink to="/approvals" className={mobileLinkClass} onClick={handleMobileLinkClick}>
                      <Fingerprint size={20} className="text-gray-400 shrink-0" />
                      <span className="font-semibold">Approval & Sign-off</span>
                    </NavLink>

                    <NavLink to="/disputes" className={mobileLinkClass} onClick={handleMobileLinkClick}>
                      <div className="relative flex items-center gap-4">
                        <AlertCircle size={20} className="text-gray-400 shrink-0" />
                        <span className="font-semibold">Dispute Management</span>
                        {disputeCount > 0 && (
                          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-black text-white ml-2">
                            {disputeCount}
                          </span>
                        )}
                      </div>
                    </NavLink>

                    <NavLink to="/communication" className={mobileLinkClass} onClick={handleMobileLinkClick}>
                      <div className="relative shrink-0">
                        <MessageSquare size={20} className="text-gray-400" />
                        {unreadCount > 0 && (
                          <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[9px] font-black text-white">
                            {unreadCount}
                          </span>
                        )}
                      </div>
                      <span className="font-semibold">Chat</span>
                    </NavLink>
                  </>
                )}

                {isSuperAdmin && (
                  <>
                    <hr className="my-3 border-gray-800" />
                    <p className="text-[10px] font-black uppercase text-amber-500/85 tracking-widest px-4 pb-1">Platform Admin</p>
                    
                    <NavLink to="/super-admin-portal" className={mobileLinkClass} onClick={handleMobileLinkClick}>
                      <Crown size={20} className="text-amber-400 shrink-0" />
                      <span className="font-semibold text-amber-300">Super Admin Portal</span>
                    </NavLink>
                  </>
                )}

                <hr className="my-3 border-gray-800" />
                <NavLink to="/guide" className={mobileLinkClass} onClick={handleMobileLinkClick}>
                  <BookOpen size={20} className="text-gray-400 shrink-0" />
                  <span className="font-semibold">User Guide & Tour</span>
                </NavLink>

                <div className="pt-4">
                  <button
                    onClick={handleSignOut}
                    className="flex items-center space-x-4 px-4 py-3.5 rounded-2xl transition-all text-base min-h-[48px] text-gray-400 hover:text-rose-400 hover:bg-rose-500/10 w-full text-left cursor-pointer font-semibold"
                  >
                    <LogOut size={20} className="shrink-0" />
                    <span>Sign Out</span>
                  </button>
                </div>

              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>


      {/* ========================================== */}
      {/* DESKTOP SIDEBAR RAIL (Viewport >= 768px ONLY) */}
      {/* ========================================== */}
      <div className="hidden md:flex md:flex-col md:w-72 bg-gray-900 text-white p-6 font-sans border-r border-gray-850 shrink-0 min-h-screen">
        <div className="flex items-center space-x-3 mb-8 px-3">
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
            <DollarSign size={20} className="text-white" />
          </div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-black tracking-tight">TipValue AI</h1>
            <span className="px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400 border border-blue-500/30 text-[10px] font-black uppercase tracking-wider">
              BETA
            </span>
          </div>
        </div>
        
        <nav className="flex-1 space-y-1 overflow-y-auto no-scrollbar">
          {isAdminOrManager ? (
            <>
              {/* SECTION 1: DAILY OPERATIONS */}
              <p className="text-[9px] font-black uppercase text-gray-400 tracking-wider px-3 pb-1.5">Daily Operations</p>

              <NavLink to="/" className={desktopLinkClass}>
                <LayoutDashboard size={18} /> 
                <span className="text-sm font-medium">Operations Dashboard</span>
              </NavLink>

              <NavLink to="/jar-tips" className={desktopLinkClass}>
                <Coins size={18} /> 
                <span className="text-sm font-medium">Submit Jar Cash Tips</span>
              </NavLink>

              <NavLink to="/pos-import" className={desktopLinkClass}>
                <Upload size={18} /> 
                <span className="text-sm font-medium">Import POS Tips</span>
              </NavLink>

              <NavLink to="/review-approve" className={desktopLinkClass}>
                <div className="relative flex items-center w-full">
                  <CheckSquare size={18} className="mr-3 shrink-0" /> 
                  <span className="text-sm font-medium">Review & Approve Tips</span>
                  {reviewApproveCount > 0 && (
                    <span className="ml-auto flex h-5 w-5 items-center justify-center rounded-full bg-amber-500 text-[10px] font-black text-white">
                      {reviewApproveCount}
                    </span>
                  )}
                </div>
              </NavLink>

              <NavLink to="/allocations" className={desktopLinkClass}>
                <div className="relative flex items-center">
                  <ShieldCheck size={18} className="mr-3" /> 
                  <span className="text-sm font-medium">Review Allocations</span>
                  {allocationCount > 0 && (
                    <span className="ml-auto flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-black text-white">
                      {allocationCount}
                    </span>
                  )}
                </div>
              </NavLink>

              <NavLink to="/disputes" className={desktopLinkClass}>
                <div className="relative flex items-center">
                  <AlertCircle size={18} className="mr-3" /> 
                  <span className="text-sm font-medium">Dispute Management</span>
                  {disputeCount > 0 && (
                    <span className="ml-auto flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-black text-white">
                      {disputeCount}
                    </span>
                  )}
                </div>
              </NavLink>

              {/* SECTION 2: PAYROLL & REPORTING */}
              <hr className="my-4 border-gray-800" />
              <p className="text-[9px] font-black uppercase text-gray-400 tracking-wider px-3 pb-1.5">Payroll and Reporting</p>

              <NavLink to="/payroll" className={desktopLinkClass}>
                <Download size={18} /> 
                <span className="text-sm font-medium">Run Tip Payroll Export</span>
              </NavLink>

              <NavLink to="/reports" className={desktopLinkClass}>
                <FileText size={18} /> 
                <span className="text-sm font-medium">Reports</span>
              </NavLink>

              <NavLink to="/shortfall" className={desktopLinkClass}>
                <Calculator size={18} /> 
                <span className="text-sm font-medium">Gross Receipts & IRS 8%</span>
              </NavLink>

              {/* SECTION 3: ANALYTICS & TEAM */}
              <hr className="my-4 border-gray-800" />
              <p className="text-[9px] font-black uppercase text-gray-400 tracking-wider px-3 pb-1.5">Analytics & Team</p>

              <NavLink to="/benchmarks" className={desktopLinkClass}>
                <BarChart2 size={18} /> 
                <span className="text-sm font-medium">Benchmark Dashboard</span>
              </NavLink>

              <NavLink to="/communication" className={desktopLinkClass}>
                <div className="relative flex items-center">
                  <MessageSquare size={18} className="mr-3" /> 
                  <span className="text-sm font-medium">Chat</span>
                  {unreadCount > 0 && (
                    <span className="ml-auto flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-black text-white">
                      {unreadCount}
                    </span>
                  )}
                </div>
              </NavLink>

              {/* SECTION 4: PERSONAL ACCOUNT */}
              <hr className="my-4 border-gray-800" />
              <p className="text-[9px] font-black uppercase text-gray-400 tracking-wider px-3 pb-1.5">Personal Account</p>

              <NavLink to="/personal" className={desktopLinkClass}>
                <Users size={18} /> 
                <span className="text-sm font-medium">My Tips</span>
              </NavLink>

              <NavLink to="/tips" className={desktopLinkClass}>
                <div className="relative flex items-center w-full">
                  <DollarSign size={18} className="mr-3 shrink-0" /> 
                  <span className="text-sm font-medium">Report My Cash Tips</span>
                  {tipCount > 0 && (
                    <span className="ml-auto flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-black text-white">
                      {tipCount}
                    </span>
                  )}
                </div>
              </NavLink>

              <NavLink to="/approvals" className={desktopLinkClass}>
                <Fingerprint size={18} />
                <span className="text-sm font-medium">Approval & Sign-off</span>
              </NavLink>

              {/* SECTION 5: MANAGEMENT */}
              <hr className="my-4 border-gray-800" />
              <p className="text-[9px] font-black uppercase text-gray-400 tracking-wider px-3 pb-1.5">Management</p>

              <NavLink to="/staff-roster" className={desktopLinkClass}>
                <Users size={18} /> 
                <span className="text-sm font-medium">Staff & Roster</span>
              </NavLink>

              {(role === 'admin' || role === 'owner' || isSuperAdmin) && (
                <NavLink to="/settings/locations" className={desktopLinkClass}>
                  <Building2 size={18} /> 
                  <span className="text-sm font-medium">Store Locations</span>
                </NavLink>
              )}

              {(role === 'admin' || role === 'owner' || isSuperAdmin) && (
                <NavLink to="/user-management" className={desktopLinkClass}>
                  <UserPlus size={18} /> 
                  <span className="text-sm font-medium">Invite Users</span>
                </NavLink>
              )}
              
              {(role === 'owner' || isSuperAdmin) && (
                <NavLink to="/settings" className={desktopLinkClass}>
                  <Settings size={18} /> 
                  <span className="text-sm font-medium">Settings</span>
                </NavLink>
              )}
            </>
          ) : (
            /* ========================================== */
            /* EMPLOYEE DESKTOP SIDEBAR NAVIGATION        */
            /* ========================================== */
            <>
              <p className="text-[9px] font-black uppercase text-gray-400 tracking-wider px-3 pb-1.5">My Workspace</p>

              <NavLink to="/" className={desktopLinkClass}>
                <LayoutDashboard size={18} /> 
                <span className="text-sm font-medium">My Dashboard</span>
              </NavLink>

              <NavLink to="/tips" className={desktopLinkClass}>
                <div className="relative flex items-center w-full">
                  <DollarSign size={18} className="mr-3 shrink-0" /> 
                  <span className="text-sm font-medium">Report My Cash Tips</span>
                  {tipCount > 0 && (
                    <span className="ml-auto flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-black text-white">
                      {tipCount}
                    </span>
                  )}
                </div>
              </NavLink>

              <NavLink to="/approvals" className={desktopLinkClass}>
                <Fingerprint size={18} />
                <span className="text-sm font-medium">Approval & Sign-off</span>
              </NavLink>

              <NavLink to="/disputes" className={desktopLinkClass}>
                <div className="relative flex items-center">
                  <AlertCircle size={18} className="mr-3" /> 
                  <span className="text-sm font-medium">Dispute Management</span>
                  {disputeCount > 0 && (
                    <span className="ml-auto flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-black text-white">
                      {disputeCount}
                    </span>
                  )}
                </div>
              </NavLink>

              <NavLink to="/communication" className={desktopLinkClass}>
                <div className="relative flex items-center">
                  <MessageSquare size={18} className="mr-3" /> 
                  <span className="text-sm font-medium">Chat</span>
                  {unreadCount > 0 && (
                    <span className="ml-auto flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-black text-white">
                      {unreadCount}
                    </span>
                  )}
                </div>
              </NavLink>
            </>
          )}

          {isSuperAdmin && (
            <>
              <hr className="my-4 border-gray-800" />
              <p className="text-[9px] font-black uppercase text-amber-500/90 tracking-wider px-3 pb-1.5">Platform Admin</p>

              <NavLink to="/super-admin-portal" className={desktopLinkClass}>
                <Crown size={18} className="text-amber-400" /> 
                <span className="text-sm font-medium text-amber-300">Super Admin Portal</span>
              </NavLink>
            </>
          )}
          <hr className="my-4 border-gray-800" />
          <NavLink to="/guide" className={desktopLinkClass}>
            <BookOpen size={18} /> 
            <span className="text-sm font-medium">User Guide & Tour</span>
          </NavLink>
        </nav>

        <div className="pt-4 border-t border-gray-800/80 mt-auto">
          <button 
            onClick={handleSignOut}
            className="flex items-center space-x-3 p-3 rounded-xl transition-all text-gray-400 hover:text-red-400 hover:bg-red-500/10 w-full text-left cursor-pointer font-medium"
          >
            <LogOut size={18} /> 
            <span className="text-sm">Sign Out</span>
          </button>
        </div>
      </div>
    </div>
  );
};
